import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasks } from '@/hooks/useTasks';
import TaskListItem from './TaskListItem';
import SetFilter from './SetFilter';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import Spinner from '@/components/common/Spinner';
import EmptyState from '@/components/common/EmptyState';

type HistoryFilter = 'history' | 'completed' | 'ignored';

export default function TaskHistory() {
  const [filter, setFilter] = useState<HistoryFilter>('history');
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);

  const { data, isLoading, error } = useTasks(filter, selectedSetId);
  const navigate = useNavigate();

  return (
    <div className="mx-auto max-w-3xl p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-neutral-900">Move History</h1>
        <Button variant="secondary" onClick={() => navigate('/app/tasks')}>
          Current Moves
        </Button>
      </div>

      {/* Set filter */}
      <div className="mb-4 flex items-center gap-2">
        <SetFilter selectedSetId={selectedSetId} onSetChange={setSelectedSetId} />
      </div>

      {/* Status filters */}
      <div className="mb-4 flex gap-2">
        {(['history', 'completed', 'ignored'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-primary-100 text-primary-700'
                : 'text-neutral-600 hover:bg-neutral-100'
            }`}
          >
            {f === 'history' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Move list */}
      {isLoading && <Spinner className="py-8" />}

      {error && (
        <div className="p-4 text-center text-sm text-error-600">Failed to load move history</div>
      )}

      {data && data.tasks.length === 0 && (
        <EmptyState title="No moves in history" description="Completed and ignored moves will appear here" />
      )}

      <div className="space-y-2">
        {data?.tasks.map((task) => (
          <div key={task.task_id} className="relative">
            <TaskListItem
              task={task}
              onClick={() => navigate(`/app/tasks/${task.task_id}`)}
              showSetName={!selectedSetId}
            />
            <div className="absolute right-4 top-3">
              <Badge variant={task.status === 'completed' ? 'success' : 'default'}>
                {task.status === 'completed' ? '✓ Done' : 'Ignored'}
              </Badge>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
